import { Download, Forward, Play } from "lucide-react";
import { shareMedia, type ShareTarget } from "@/lib/mediaUtils";

interface MediaGridItemProps {
  imageUrl?: string;
  videoUrl?: string;
  hoverText?: string;
  isVideo?: boolean;
  onClick: () => void;
  /** When provided, renders a small download icon in the top-end corner. */
  onDownload?: () => void;
  /** Accessible label for the download button (e.g. localized "Download"). */
  downloadLabel?: string;
  /** Accessible label for the share button (defaults to "Share" if omitted). */
  shareLabel?: string;
  /**
   * Required to render the share button. Passing { id, type } here ensures the
   * share opens a branded `/s/:id` URL instead of leaking the raw CDN file.
   */
  shareTarget?: ShareTarget;
  alt?: string;
  aspectRatio?: "9/16" | "square";
  children?: React.ReactNode;
}

export function MediaGridItem({
  imageUrl,
  videoUrl,
  hoverText,
  isVideo,
  onClick,
  onDownload,
  downloadLabel,
  shareLabel,
  shareTarget,
  alt,
  aspectRatio = "9/16",
  children,
}: MediaGridItemProps) {
  return (
    <div
      onClick={onClick}
      className={`relative group border-[2px] border-border bg-card cursor-pointer overflow-hidden ${
        aspectRatio === "square" ? "aspect-square rounded-xl" : "aspect-[9/16]"
      }`}
    >
      {imageUrl ? (
        <img
          src={imageUrl}
          loading="lazy"
          className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
          alt={alt ?? ""}
        />
      ) : videoUrl ? (
        <video
          src={`${videoUrl}#t=0.1`}
          muted
          playsInline
          preload="metadata"
          className="w-full h-full object-cover"
        />
      ) : (
        <div className="w-full h-full bg-gradient-to-br from-muted to-muted/60" />
      )}

      {isVideo ? (
        <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition-all duration-300 flex items-center justify-center">
          <div className="w-12 h-12 md:w-14 md:h-14 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center">
            <Play className="w-6 h-6 md:w-7 md:h-7 text-white ml-0.5" fill="white" />
          </div>
        </div>
      ) : (
        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-all duration-300 flex items-center justify-center">
          {hoverText && (
            <span className="text-white font-bold text-xs md:text-sm">
              {hoverText}
            </span>
          )}
        </div>
      )}

      {onDownload && (
        <div className="absolute top-2 start-2 z-10 flex gap-1.5">
          <button
            type="button"
            aria-label={downloadLabel ?? "Download"}
            onClick={(e) => {
              e.stopPropagation();
              onDownload();
            }}
            className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-black/65 backdrop-blur-sm text-white transition-all duration-200 hover:bg-black/85 hover:scale-105 active:scale-95 shadow-md ring-1 ring-white/15"
          >
            <Download className="w-4 h-4" />
          </button>
          {shareTarget && (
            <button
              type="button"
              aria-label={shareLabel ?? "Share"}
              onClick={(e) => {
                e.stopPropagation();
                void shareMedia(shareTarget);
              }}
              className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-black/65 backdrop-blur-sm text-white transition-all duration-200 hover:bg-black/85 hover:scale-105 active:scale-95 shadow-md ring-1 ring-white/15"
            >
              <Forward className="w-4 h-4" />
            </button>
          )}
        </div>
      )}

      {children}
    </div>
  );
}
